/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, ReactElement, useEffect } from 'react';
import {
  Grid,
  CircularProgress,
  Typography,
  Button,
  TextField,
  Fade,
} from '@material-ui/core';
import {
  browserName,
  browserVersion,
  osName,
  osVersion,
} from 'react-device-detect';
import { Link, RouteComponentProps } from 'react-router-dom';
import {
  EQueryStringKey,
  EQueryStringValue,
  QueryStringService,
} from 'services/QueryString/QueryStringService';
import { GoogleAuthService } from 'services/Auth/GoogleAuthService';
import { EmailErrorService } from 'services/EmailError/EmailErrorService';
import useStyles from './styles';
import logo from '../../assets/images/logo.png';
import googleLogo from '../../assets/images/google.svg';
import { AuthService } from '../../services/Auth/AuthService';
import { useUserData } from '../../hooks/context/userUserData.hook';

interface ILoginProps {
  history: RouteComponentProps['history'];
}

export default function Login({ history }: ILoginProps): ReactElement {
  const classes = useStyles();

  const [, setUserData] = useUserData();
  const fulfillDependencies = AuthService.useDependenciesFetch();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');

  const login = async () => {
    setError(false);
    setIsLoading(true);

    try {
      if (!emailValue && !passwordValue) throw Error('Field empty');

      const userData = await AuthService.getUserData(emailValue);
      const userDataWithSlashes: string = (JSON.stringify(
        userData,
      ) as any).replaceAll('"', '\\"');

      const token = await AuthService.login(
        emailValue,
        passwordValue,
        userDataWithSlashes,
      );
      const user = AuthService.persistData(token, setUserData!);
      await fulfillDependencies(user.id);

      history.push('/');
    } catch (e) {
      setError(true);
      setIsLoading(false);

      EmailErrorService.sendMailError({
        user_id: '',
        user_role: '',
        user_email: emailValue,
        user_name: '',
        location: window.location.href,
        os: osName,
        os_version: osVersion,
        browser_version: browserVersion,
        browser_name: browserName,
        error: e.toString(),
        error_info: e.stack,
      });
    }
  };

  const googleAuthInitialization = async () => {
    try {
      const googleAuthResult = QueryStringService.get<string>(
        EQueryStringKey.GoogleAuthResult,
      );

      if (googleAuthResult === EQueryStringValue.Success) {
        AuthService.cleanLocalStorageAuthData();

        const googleAuthToken =
          QueryStringService.get<string>(EQueryStringKey.GoogleAccessToken) ||
          '';

        QueryStringService.delete(EQueryStringKey.GoogleAuthResult);
        QueryStringService.delete(EQueryStringKey.GoogleAccessToken);

        const user = AuthService.persistData(googleAuthToken, setUserData!);
        await fulfillDependencies(user.id);

        history.push('/');
      }
    } catch (e) {
      EmailErrorService.sendMailError({
        user_id: '',
        user_role: '',
        user_email: 'Login with Google',
        user_name: '',
        location: window.location.href,
        os: osName,
        os_version: osVersion,
        browser_version: browserVersion,
        browser_name: browserName,
        error: e.toString(),
        error_info: e.stack,
      });
    }
  };

  useEffect(() => {
    let mounted = true;

    AuthService.cleanLocalStorageAuthData();

    if (mounted) {
      googleAuthInitialization();
    }

    return () => (mounted = false) as any;
  /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  return (
    <Grid container className={classes.container}>
      <div className={classes.logotypeContainer}>
        <img src={logo} alt="logo" className={classes.logotypeImage} />
        <Typography className={classes.logotypeText}>Adireto</Typography>
        <Typography className={classes.subLogoTypeText}>Content</Typography>
      </div>
      <div className={classes.formContainer}>
        <div className={classes.form}>
          <div className={classes.titleContainer}>
            <Typography className={classes.title}>LOGIN</Typography>
          </div>

          <Typography variant="h1" className={classes.greeting}>
            Hello User
          </Typography>

          {error ||
          QueryStringService.get<string>(EQueryStringKey.GoogleAuthResult) ===
            EQueryStringValue.Error ? (
              <Fade in>
                <Typography color="secondary" className={classes.errorMessage}>
                  Something is wrong with your login or password :(
                </Typography>
              </Fade>
            ) : (
              <></>
            )}

          <TextField
            id="email"
            InputProps={{
              classes: {
                root: classes.firstTextField,
                underline: classes.textFieldUnderline,
                input: classes.textField,
              },
            }}
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            margin="normal"
            placeholder="Email Address"
            type="email"
            fullWidth
          />
          <TextField
            id="password"
            InputProps={{
              classes: {
                underline: classes.textFieldUnderline,
                input: classes.textField,
              },
            }}
            value={passwordValue}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                login();
              }
            }}
            onChange={(e) => setPasswordValue(e.target.value)}
            margin="normal"
            placeholder="Password"
            type="password"
            fullWidth
          />

          <div className={classes.formButtons}>
            {isLoading ? (
              <CircularProgress size={26} className={classes.loginLoader} />
            ) : (
              <Button
                disabled={emailValue.length === 0 || passwordValue.length === 0}
                onClick={login}
                variant="contained"
                color="primary"
                size="large"
                style={{ width: '100%' }}
              >
                Login
              </Button>
            )}
          </div>

          <div className={classes.separator}>OR</div>

          <a
            href={GoogleAuthService.getGoogleAuthUrl()}
            className={classes.googleLoginButton}
          >
            <div className="google-icon-wrapper">
              <img
                className="google-icon"
                alt="google-login"
                src={googleLogo}
              />
            </div>
            <div className="btn-text">
              <b>Continue with Google</b>
            </div>
          </a>

          <Link
            to={{ pathname: '/subscribe' }}
            className={classes.signUpButton}
          >
            Sign up here!
          </Link>
        </div>
        <Typography color="primary" className={classes.copyright}>
          © {new Date().getFullYear()} Adireto. All rights reserved.
        </Typography>
      </div>
    </Grid>
  );
}
